import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Radio,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import DialogContainer from "../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../components/textinput/textinput";
import { roleRequest, resetRoles } from "../../../../redux/roles/slice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { permissionAccessLevels } from "../../../../utils/Constant";
import Loading from "../../../../components/loading/loading";
import { addInternalUserRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { getAllInspectionFirmsRequest } from "../../../../redux/inspection-firms/slice";

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  userType: number;
  roleIds: string[];
  inspectorFirmId: string;
}

type props = {
  showDialog: boolean;
  closeDialog: any;
  addUser?: any;
  inspectorFirmId?: string;
};

export default function CreateInspectorUser({
  showDialog,
  closeDialog,
  addUser,
  inspectorFirmId,
}: props) {
  const dispatch = useDispatch();

  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { allRoles, loading: rolesLoading } = useSelector(
    (state: any) => state.roles
  );

  const { loading, allInspectionFirms } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .required("This field can't be empty"),
    firstName: Yup.string().required("This field can't be empty"),
    lastName: Yup.string().required("This field can't be empty"),
    inspectorFirmId: Yup.string().required("This field can't be empty"),
    roleIds: Yup.array()
      .min(1, "At least one role must be selected")
      .required("This field can't be empty"),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      title: "",
      userType: 2,
      roleIds: [],
      inspectorFirmId: inspectorFirmId || "",
    },
    validationSchema,
    onSubmit: (values) => {
      addUserRequest(values);
    },
  });

  const addUserRequest = async (data: FormValues) => {
    setShowLoading(true);
    try {
      const response = await addInternalUserRequest(data);
      if (response?.status === 201) {
        addUser(response?.data);
        hideDialog();
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      } else {
        setShowErrorNotification(true);
        setErrorMessage("Failed to create user. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setShowLoading(false);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    dispatch(resetRoles());
    closeDialog();
  };

  const handleRoleChange = (roleId: string, checked: boolean) => {
    const currentRoleIds = [...formik.values.roleIds];

    if (checked) {
      if (!currentRoleIds.includes(roleId)) {
        currentRoleIds.push(roleId);
      }
    } else {
      const index = currentRoleIds.indexOf(roleId);
      if (index > -1) {
        currentRoleIds.splice(index, 1);
      }
    }

    formik.setFieldValue("roleIds", currentRoleIds);
  };

  const permissions = React.useMemo(() => {
    if (
      !allRoles ||
      allRoles.length === 0 ||
      formik.values.roleIds.length === 0
    )
      return [];

    const selectedPermissionsMap = new Map();

    allRoles.forEach((role: any) => {
      if (
        formik.values.roleIds.includes(role.id) &&
        role.permissions &&
        role.permissions.length > 0
      ) {
        role.permissions.forEach((permission: any) => {
          const categoryId = permission.category?.id;
          if (categoryId) {
            if (!selectedPermissionsMap.has(categoryId)) {
              selectedPermissionsMap.set(categoryId, {
                id: categoryId,
                name: permission.category.name,
                permissions: [],
              });
            }

            const existingPermission = selectedPermissionsMap
              .get(categoryId)
              .permissions.find(
                (p: any) => p.resourceId === permission.resourceId
              );

            if (!existingPermission) {
              selectedPermissionsMap.get(categoryId).permissions.push({
                ...permission,
                roleIds: [role.id],
              });
            } else {
              existingPermission.roleIds.push(role.id);
            }
          }
        });
      }
    });

    return Array.from(selectedPermissionsMap.values());
  }, [allRoles, formik.values.roleIds]);

  useEffect(() => {
    if (showDialog) {
      dispatch(roleRequest("Inspector"));
      !inspectorFirmId &&
        dispatch(getAllInspectionFirmsRequest({ page: 1, pageSize: 99999 }));
    }
  }, [showDialog, inspectorFirmId]); // eslint-disable-line

  return (
    <DialogContainer
      title="Add Inspection Firm User"
      showDialog={showDialog}
      closeDialog={hideDialog}
      buttonText="Add User"
      onSubmit={formik.handleSubmit}
    >
      <Box>
        <Typography className="user-details-txt">User Details</Typography>
        <Box className="user-name-layout">
          <Typography className="label-txt">Title</Typography>
          <TextInput
            className="field-input"
            id="title"
            label="Title"
            variant="outlined"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
          />
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="label-txt">Name</Typography>
          <Box sx={{ display: "flex", width: "calc(100% - 180px)" }}>
            <TextInput
              sx={{ flex: 1, mr: 2 }}
              id="firstName"
              label="First Name"
              variant="outlined"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
            <TextInput
              sx={{ flex: 1 }}
              id="lastName"
              label="Last Name"
              variant="outlined"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Email</Typography>
          <TextInput
            className="field-input"
            id="email"
            label="Username"
            variant="outlined"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Box>
        <Divider />
        {!inspectorFirmId && (
          <>
            <Box className="user-name-layout">
              <Typography className="label-txt">Inspection Firm</Typography>
              <FormGroup sx={{ width: "calc(100% - 180px)" }}>
                <FormControl>
                  <InputLabel id="client-label">Inspection Firm</InputLabel>
                  <Select
                    labelId="client-label"
                    id="inspectorFirmId"
                    name="inspectorFirmId"
                    value={formik.values.inspectorFirmId}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.inspectorFirmId &&
                      Boolean(formik.errors.inspectorFirmId)
                    }
                    label="Inspection Firm"
                  >
                    {allInspectionFirms &&
                      allInspectionFirms?.totalCount > 0 &&
                      Array.isArray(allInspectionFirms?.items) &&
                      allInspectionFirms?.items.map((firm: any) => (
                        <MenuItem key={firm.id} value={firm.id}>
                          {firm.displayName}
                        </MenuItem>
                      ))}
                  </Select>
                  {formik.touched.inspectorFirmId &&
                    formik.errors.inspectorFirmId && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {formik.errors.inspectorFirmId}
                      </Typography>
                    )}
                </FormControl>
              </FormGroup>
            </Box>
            <Divider />
          </>
        )}

        <Typography className="user-details-txt">Roles</Typography>
        <Box className="user-name-layout">
          <Typography className="label-txt">Role(s)</Typography>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <FormGroup sx={{ display: "block", width: 620 }}>
              {allRoles?.map((role: any) => (
                <FormControlLabel
                  key={role.id}
                  sx={{ width: 300 }}
                  control={
                    <Checkbox
                      name={role.id}
                      checked={formik.values.roleIds.includes(role.id)}
                      onChange={(e) =>
                        handleRoleChange(role.id, e.target.checked)
                      }
                    />
                  }
                  label={role.name}
                />
              ))}
            </FormGroup>
            {formik.touched.roleIds && formik.errors.roleIds && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {formik.errors.roleIds}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />
        {permissions.length > 0 && (
          <Accordion sx={{ border: "none", boxShadow: "none" }} defaultExpanded>
            <AccordionSummary
              sx={{
                marginTop: -2,
                padding: 0,
                minHeight: 48,
                ".MuiAccordionSummary-content.Mui-expanded": { margin: 0 },
              }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="permission-content"
              id="permission-header"
            >
              <Typography className="user-details-txt">Permissions</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <Box
                className="permission-header"
                sx={{ width: "calc(100% - 192px)", marginLeft: 24 }}
              >
                <Typography
                  sx={{ flex: 1, textAlign: "start", fontWeight: "bold" }}
                >
                  Permission
                </Typography>
                <Typography className="permission-header-txt">
                  Full Access
                </Typography>
                <Typography className="permission-header-txt">
                  View Only
                </Typography>
                <Typography className="permission-header-txt">None</Typography>
              </Box>

              {permissions.map((category: any) => {
                return (
                  <React.Fragment key={category.id}>
                    <Box className="user-name-layout">
                      <Typography className="label-txt" sx={{ mt: 1 }}>
                        {category?.name}
                      </Typography>
                      <Box
                        display={"block"}
                        sx={{ width: "calc(100% - 192px)" }}
                      >
                        {category.permissions.map(
                          (item: any, index: number) => (
                            <React.Fragment
                              key={`${category.id}-${item.resourceId || index}`}
                            >
                              <Box className="permission-row">
                                <Box className="permission-title">
                                  <Typography>{item?.name}</Typography>
                                </Box>
                                <Radio
                                  className="permission-radio"
                                  value="full-access"
                                  name={`permission-${item.resourceId}`}
                                  inputProps={{ "aria-label": "A" }}
                                  checked={
                                    item.accessLevel ===
                                    permissionAccessLevels.fullAccess
                                  }
                                  disabled
                                />
                                <Radio
                                  className="permission-radio"
                                  value="view-only"
                                  name={`permission-${item.resourceId}`}
                                  inputProps={{ "aria-label": "B" }}
                                  checked={
                                    item.accessLevel ===
                                    permissionAccessLevels.viewOnly
                                  }
                                  disabled
                                />
                                <Radio
                                  className="permission-radio"
                                  value="none"
                                  name={`permission-${item.resourceId}`}
                                  inputProps={{ "aria-label": "C" }}
                                  checked={
                                    item.accessLevel ===
                                    permissionAccessLevels.noAccess
                                  }
                                  disabled
                                />
                              </Box>
                              {index !== category.permissions.length - 1 && (
                                <Divider />
                              )}
                            </React.Fragment>
                          )
                        )}
                      </Box>
                    </Box>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
      {(loading || showLoading || rolesLoading) && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </DialogContainer>
  );
}
